// ========================================
// CENTRAL ERROR PROCESSING SERVICE
// ========================================
// Main error handling service with Firebase integration and user-friendly messaging

import {
  StandardError,
  ErrorCategory,
  ErrorSeverity,
  ErrorRecoveryAction,
  ErrorContext,
  ErrorRecoveryOptions,
  ErrorHandlerConfig,
  FIREBASE_ERROR_PATTERNS,
  ERROR_PATTERNS,
  ErrorPattern,
} from '../types/errorTypes'
import { loggingService } from './logging'

interface ErrorServiceConfig extends ErrorHandlerConfig {
  environment: 'development' | 'staging' | 'production'
  enablePerformanceTracking: boolean
  enableErrorAnalytics: boolean
}

class ErrorService {
  private config: ErrorServiceConfig
  private errorHistory: Map<string, StandardError> = new Map()
  private retryCounters: Map<string, number> = new Map()
  private listeners: Array<(error: StandardError) => void> = []

  constructor(config: Partial<ErrorServiceConfig> = {}) {
    this.config = {
      enableConsoleLogging: true,
      enableRemoteLogging: false,
      enableUserNotifications: true,
      defaultRetryCount: 3,
      defaultRetryDelayMs: 1000,
      enableAutomaticRetry: true,
      logToLocalStorage: true,
      maxLocalStorageEntries: 100,
      environment:
        (import.meta.env.MODE as 'development' | 'staging' | 'production') ||
        'development',
      enablePerformanceTracking: true,
      enableErrorAnalytics: true,
      ...config,
    }
  }

  // ========================================
  // ERROR CREATION AND PROCESSING
  // ========================================

  public createError(
    code: string,
    message: string,
    category: ErrorCategory = ErrorCategory.SYSTEM,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    originalError?: Error,
    context: Partial<ErrorContext> = {}
  ): StandardError {
    const errorId = this.generateErrorId()
    const timestamp = new Date().toISOString()

    const standardError: StandardError = {
      id: errorId,
      code,
      category,
      severity,
      message,
      userMessage: this.generateUserMessage(code, category, message),
      technicalMessage: originalError?.message || message,
      recoveryAction: this.determineRecoveryAction(
        category,
        code,
        originalError
      ),
      context: {
        timestamp,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...context,
      },
      originalError,
      stack: originalError?.stack || new Error().stack,
      retryable: this.isRetryable(category, code, originalError),
      retryCount: 0,
      maxRetries: this.config.defaultRetryCount,
    }

    this.processError(standardError)
    return standardError
  }

  public processFirebaseError(
    firebaseError: unknown,
    context: Partial<ErrorContext> = {}
  ): StandardError {
    const errorObj =
      firebaseError instanceof Error
        ? firebaseError
        : new Error(String(firebaseError))
    const errorCode =
      (firebaseError as { code?: string }).code || 'unknown-firebase-error'
    const errorMapping = FIREBASE_ERROR_PATTERNS[errorCode]

    if (errorMapping) {
      return this.createError(
        errorMapping.code,
        errorObj.message || errorMapping.userMessage,
        errorMapping.category,
        errorMapping.severity,
        errorObj,
        context
      )
    }

    // Handle unmapped Firebase errors
    const category = this.categorizeFirebaseError(errorCode)
    const severity = this.determineSeverity(errorObj)

    return this.createError(
      errorCode,
      errorObj.message || 'An unexpected error occurred',
      category,
      severity,
      errorObj,
      context
    )
  }

  public processGenericError(
    error: Error,
    context: Partial<ErrorContext> = {}
  ): StandardError {
    const category = this.categorizeGenericError(error)
    const severity = this.determineSeverity(error)
    const code = this.generateErrorCode(error)

    return this.createError(
      code,
      error.message,
      category,
      severity,
      error,
      context
    )
  }

  private processError(error: StandardError): void {
    // Store in history
    this.errorHistory.set(error.id, error)

    // Log the error
    if (this.config.enableConsoleLogging || this.config.logToLocalStorage) {
      loggingService.logError(error)
    }

    // Notify listeners
    this.notifyListeners(error)

    // Track analytics
    if (this.config.enableErrorAnalytics) {
      this.trackErrorAnalytics(error)
    }

    // Automatic retry logic
    if (error.retryable && this.config.enableAutomaticRetry) {
      this.scheduleRetry(error)
    }
  }

  // ========================================
  // ERROR CATEGORIZATION AND MAPPING
  // ========================================

  private categorizeFirebaseError(errorCode: string): ErrorCategory {
    if (errorCode.startsWith('auth/')) {
      if (
        errorCode.includes('permission') ||
        errorCode.includes('unauthorized')
      ) {
        return ErrorCategory.AUTHORIZATION
      }
      return ErrorCategory.AUTHENTICATION
    }

    if (errorCode.startsWith('firestore/')) {
      if (errorCode.includes('permission')) {
        return ErrorCategory.AUTHORIZATION
      }
      if (errorCode.includes('unavailable') || errorCode.includes('deadline')) {
        return ErrorCategory.NETWORK
      }
      return ErrorCategory.DATABASE
    }

    if (errorCode.startsWith('functions/')) {
      if (errorCode.includes('unauthenticated')) {
        return ErrorCategory.AUTHENTICATION
      }
      if (errorCode.includes('permission')) {
        return ErrorCategory.AUTHORIZATION
      }
      return ErrorCategory.EXTERNAL_API
    }

    if (errorCode.startsWith('storage/')) {
      return ErrorCategory.STORAGE
    }

    return ErrorCategory.FIREBASE
  }

  private categorizeGenericError(error: Error): ErrorCategory {
    const message = error.message.toLowerCase()
    const stack = error.stack?.toLowerCase() || ''

    // Check against error patterns
    for (const [pattern, regex] of Object.entries(ERROR_PATTERNS)) {
      if (regex.test(message) || regex.test(stack)) {
        switch (pattern as ErrorPattern) {
          case 'NETWORK_TIMEOUT':
            return ErrorCategory.NETWORK
          case 'PERMISSION_DENIED':
            return ErrorCategory.AUTHORIZATION
          case 'NOT_FOUND':
            return ErrorCategory.SYSTEM
          case 'RATE_LIMITED':
            return ErrorCategory.RATE_LIMIT
          case 'VALIDATION':
            return ErrorCategory.VALIDATION
          case 'CHUNK_LOAD':
            return ErrorCategory.CHUNK_LOAD
          case 'CORS':
            return ErrorCategory.NETWORK
        }
      }
    }

    // Check error name
    if (error.name === 'ChunkLoadError') {
      return ErrorCategory.CHUNK_LOAD
    }

    if (error.name === 'NetworkError') {
      return ErrorCategory.NETWORK
    }

    if (error.name === 'ValidationError') {
      return ErrorCategory.VALIDATION
    }

    return ErrorCategory.SYSTEM
  }

  private determineSeverity(error: Error & { code?: string }): ErrorSeverity {
    const errorCode = error.code || error.name || ''
    const message = error.message || ''

    // Critical errors
    if (
      errorCode.includes('resource-exhausted') ||
      errorCode.includes('quota-exceeded') ||
      message.includes('fatal')
    ) {
      return ErrorSeverity.CRITICAL
    }

    // High severity errors
    if (
      errorCode.includes('permission-denied') ||
      errorCode.includes('unauthorized') ||
      errorCode.includes('unavailable') ||
      message.includes('network')
    ) {
      return ErrorSeverity.HIGH
    }

    // Low severity errors
    if (
      errorCode.includes('invalid-email') ||
      errorCode.includes('weak-password') ||
      errorCode.includes('popup-closed')
    ) {
      return ErrorSeverity.LOW
    }

    return ErrorSeverity.MEDIUM
  }

  private determineRecoveryAction(
    category: ErrorCategory,
    code: string,
    _originalError?: Error
  ): ErrorRecoveryAction {
    // Check Firebase patterns first
    const firebaseMapping = FIREBASE_ERROR_PATTERNS[code]
    if (firebaseMapping) {
      return firebaseMapping.recoveryAction
    }

    // Determine by category
    switch (category) {
      case ErrorCategory.CHUNK_LOAD:
        return ErrorRecoveryAction.RELOAD
      case ErrorCategory.AUTHENTICATION:
        return code.includes('unauthenticated')
          ? ErrorRecoveryAction.LOGOUT
          : ErrorRecoveryAction.NONE
      case ErrorCategory.AUTHORIZATION:
        return ErrorRecoveryAction.CONTACT_SUPPORT
      case ErrorCategory.NETWORK:
      case ErrorCategory.RATE_LIMIT:
        return ErrorRecoveryAction.RETRY
      case ErrorCategory.VALIDATION:
      case ErrorCategory.USER_INPUT:
        return ErrorRecoveryAction.NONE
      default:
        return ErrorRecoveryAction.RETRY
    }
  }

  private isRetryable(
    category: ErrorCategory,
    code: string,
    _originalError?: Error
  ): boolean {
    // Check Firebase patterns
    const firebaseMapping = FIREBASE_ERROR_PATTERNS[code]
    if (firebaseMapping) {
      return firebaseMapping.retryable
    }

    // Determine by category
    const retryableCategories = [
      ErrorCategory.NETWORK,
      ErrorCategory.RATE_LIMIT,
      ErrorCategory.DATABASE,
      ErrorCategory.EXTERNAL_API,
      ErrorCategory.CHUNK_LOAD,
    ]

    return retryableCategories.includes(category)
  }

  private generateUserMessage(
    code: string,
    category: ErrorCategory,
    _technicalMessage: string
  ): string {
    // Check Firebase patterns first
    const firebaseMapping = FIREBASE_ERROR_PATTERNS[code]
    if (firebaseMapping) {
      return firebaseMapping.userMessage
    }

    // Generate user-friendly messages by category
    switch (category) {
      case ErrorCategory.AUTHENTICATION:
        return 'Please sign in to continue.'
      case ErrorCategory.AUTHORIZATION:
        return "You don't have permission to perform this action."
      case ErrorCategory.VALIDATION:
        return 'Please check your input and try again.'
      case ErrorCategory.NETWORK:
        return 'Network connection error. Please check your internet connection and try again.'
      case ErrorCategory.DATABASE:
        return 'Database temporarily unavailable. Please try again in a few moments.'
      case ErrorCategory.RATE_LIMIT:
        return 'Too many requests. Please wait a moment and try again.'
      case ErrorCategory.CHUNK_LOAD:
        return 'Failed to load application resources. Please refresh the page.'
      case ErrorCategory.EXTERNAL_API:
        return 'External service temporarily unavailable. Please try again later.'
      case ErrorCategory.STORAGE:
        return 'File operation failed. Please try again.'
      default:
        return 'An unexpected error occurred. Please try again or contact support if the problem persists.'
    }
  }

  private generateErrorCode(error: Error): string {
    const name = error.name.toLowerCase().replace(/error$/, '')
    const timestamp = Date.now().toString(36)
    return `${name}-${timestamp}`
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // ========================================
  // RETRY LOGIC
  // ========================================

  public async retry<T>(
    operation: () => Promise<T>,
    options: ErrorRecoveryOptions = {}
  ): Promise<T> {
    const {
      retryCount = 0,
      maxRetries = this.config.defaultRetryCount,
      retryDelayMs = this.config.defaultRetryDelayMs,
      exponentialBackoff = true,
      onRetry,
      onMaxRetriesReached,
    } = options

    try {
      return await operation()
    } catch (error) {
      const standardError =
        error instanceof Error
          ? this.processGenericError(error)
          : this.createError('unknown-error', 'Unknown error occurred')

      if (retryCount >= maxRetries || !standardError.retryable) {
        if (onMaxRetriesReached) {
          onMaxRetriesReached()
        }
        throw standardError
      }

      const delay = exponentialBackoff
        ? retryDelayMs * Math.pow(2, retryCount)
        : retryDelayMs

      if (onRetry) {
        onRetry()
      }

      loggingService.info(`Retrying operation after ${delay}ms`, {
        attempt: retryCount + 1,
        maxRetries,
        errorId: standardError.id,
      })

      await this.delay(delay)

      return this.retry(operation, {
        ...options,
        retryCount: retryCount + 1,
      })
    }
  }

  private scheduleRetry(error: StandardError): void {
    if (
      !error.retryable ||
      (error.retryCount || 0) >= (error.maxRetries || 0)
    ) {
      return
    }

    const retryKey = `${error.code}-${error.context.url}`
    const currentRetryCount = this.retryCounters.get(retryKey) || 0

    if (currentRetryCount >= this.config.defaultRetryCount) {
      return
    }

    this.retryCounters.set(retryKey, currentRetryCount + 1)

    const delay =
      this.config.defaultRetryDelayMs * Math.pow(2, currentRetryCount)

    setTimeout(() => {
      loggingService.info('Automatic retry scheduled', {
        errorId: error.id,
        attempt: currentRetryCount + 1,
        delay,
      })
    }, delay)
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // ========================================
  // ERROR RECOVERY ACTIONS
  // ========================================

  public executeRecoveryAction(error: StandardError): void {
    switch (error.recoveryAction) {
      case ErrorRecoveryAction.RELOAD:
        window.location.reload()
        break
      case ErrorRecoveryAction.REDIRECT:
        window.location.href = '/'
        break
      case ErrorRecoveryAction.LOGOUT:
        // This should be handled by the calling component
        loggingService.info('Logout recovery action triggered', {
          errorId: error.id,
        })
        break
      case ErrorRecoveryAction.CONTACT_SUPPORT:
        // This should open support dialog/page
        loggingService.info('Contact support recovery action triggered', {
          errorId: error.id,
        })
        break
      case ErrorRecoveryAction.RETRY:
        // This should be handled by the calling component
        loggingService.info('Retry recovery action triggered', {
          errorId: error.id,
        })
        break
      case ErrorRecoveryAction.NONE:
      default:
        // No action needed
        break
    }
  }

  // ========================================
  // LISTENERS AND NOTIFICATIONS
  // ========================================

  public addErrorListener(listener: (error: StandardError) => void): void {
    this.listeners.push(listener)
  }

  public removeErrorListener(listener: (error: StandardError) => void): void {
    const index = this.listeners.indexOf(listener)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  private notifyListeners(error: StandardError): void {
    this.listeners.forEach(listener => {
      try {
        listener(error)
      } catch (listenerError) {
        console.warn('Error in error listener:', listenerError)
      }
    })
  }

  // ========================================
  // ANALYTICS AND REPORTING
  // ========================================

  private trackErrorAnalytics(error: StandardError): void {
    // Track error analytics (could be integrated with Google Analytics, etc.)
    if (typeof window !== 'undefined' && 'gtag' in window) {
      try {
        ;(
          window as unknown as {
            gtag: (
              command: string,
              action: string,
              params: Record<string, unknown>
            ) => void
          }
        ).gtag('event', 'exception', {
          description: `${error.category}/${error.code}`,
          fatal: error.severity === ErrorSeverity.CRITICAL,
          custom_map: {
            error_id: error.id,
            error_category: error.category,
            error_severity: error.severity,
          },
        })
      } catch {
        // Silent fail for analytics
      }
    }
  }

  public getErrorHistory(): StandardError[] {
    return Array.from(this.errorHistory.values())
  }

  public getErrorById(errorId: string): StandardError | undefined {
    return this.errorHistory.get(errorId)
  }

  public clearErrorHistory(): void {
    this.errorHistory.clear()
    this.retryCounters.clear()
  }

  public getErrorStatistics() {
    const errors = this.getErrorHistory()
    const now = new Date()
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)

    return {
      total: errors.length,
      byCategory: this.groupBy(errors, 'category'),
      bySeverity: this.groupBy(errors, 'severity'),
      recent: errors.filter(e => new Date(e.context.timestamp) > oneHourAgo)
        .length,
      retryable: errors.filter(e => e.retryable).length,
    }
  }

  private groupBy<T>(array: T[], key: keyof T): Record<string, number> {
    return array.reduce(
      (acc, item) => {
        const value = String(item[key])
        acc[value] = (acc[value] || 0) + 1
        return acc
      },
      {} as Record<string, number>
    )
  }
}

// Create singleton instance
export const errorService = new ErrorService()

// Export convenience methods
export const {
  createError,
  processFirebaseError,
  processGenericError,
  retry,
  executeRecoveryAction,
  addErrorListener,
  removeErrorListener,
} = errorService

export default errorService
